import PageHead from '@/components/PageHead';
import {ANDROID_META_DATA} from '@/util/metaData/mobile';
import {stripHtmlForJsonLd} from '@/util/seo-utils';
import sectionBg from '@/styles/sectionBackgrounds.module.scss';
import androidStyles from '@/styles/androidAppDevelopment.module.scss';

// Sections are imported statically on purpose: next/dynamic gives every section its own render-blocking CSS file;
// static imports ship one page stylesheet. The JS is MUI-free and small.
import HeroVideoBanner from '@/components/plain-sections/HeroVideoBanner';
import ClientServed from '@/components/home-plain/ClientServed';
import KeyFacts from '@/components/plain-sections/KeyFacts';
import MvpEndToEndServices from '@/components/plain-sections/MvpEndToEndServices';
import HomeIndustryTabs from '@/components/home-plain/HomeIndustryTabs';
import IosSuccessStories from '@/components/plain-sections/IosSuccessStories';
import MvpTestimonials from '@/components/plain-sections/MvpTestimonials';
import HomeExpertiseShowcase from '@/components/home-plain/HomeExpertiseShowcase';
import FeatureIntegrationTabs from '@/components/plain-sections/FeatureIntegrationTabs';
import MvpProcess from '@/components/Ui-components/mvp-development/MvpProcess';
import HomeImageCTA from '@/components/home-plain/HomeImageCTA';
import AiStackTable from '@/components/plain-sections/AiStackTable';
import ComplianceBadgeTabs from '@/components/plain-sections/ComplianceBadgeTabs';
import MvpWhyChoose from '@/components/Ui-components/mvp-development/MvpWhyChoose';
import MvpCostTimeline from '@/components/plain-sections/MvpCostTimeline';
import HomeFAQSplit from '@/components/home-plain/HomeFAQSplit';
import LatestInsights from '@/components/plain-sections/LatestInsights';

const AndroidAppDevelopment: any = (props: any) => {
    const {
        openForm,
        clients,
        testimonials,
        androidHeroImage,
        androidHeroAspectRatio,
        androidTrustStats,
        androidServicesImage,
        androidServicesImageCaption,
        androidServices,
        androidIndustries,
        successStories,
        androidAiCapabilities,
        androidFeatures,
        androidFeatureChips,
        androidIntegrations,
        androidProcessSteps,
        androidEngineersCtaImage,
        androidTechStackRows,
        androidComplianceGroups,
        androidWhyChooseCards,
        androidCostHighlight,
        androidCostEstimator,
        androidFinalCtaImage,
        androidFaqData
    } = props;

    return (
        <div>
            <PageHead meta={ANDROID_META_DATA} url="/android-app-development" />

            <HeroVideoBanner
                heading="Android App Development Company"
                subheading="Partner with our Android app development team to build secure, high-performing applications tailored to your business workflows, integrated with existing systems and designed to scale. From product strategy and Kotlin development to modernisation, QA, deployment and ongoing support, we provide end-to-end Android app development expertise."
                primaryButtonText="Discuss Your Android Project"
                onPrimaryClick={openForm}
                secondaryButtonText="Explore Our Portfolio"
                secondaryHref="/portfolio"
                backgroundImage={androidHeroImage}
                hideBackgroundImageOnTablet
                layout="split"
                splitBadgesPlacement="below-content"
                splitImageWash={false}
                splitImageAspectRatio={androidHeroAspectRatio}
                backgroundPosition="right center"
            />
            <div className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
                <ClientServed heading="" contentData={clients} flush />
            </div>
            <div className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding bg-panel-blue">
                <KeyFacts heading="Scale and delivery evidence at a glance." facts={androidTrustStats} />
            </div>

            <div className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
                <MvpEndToEndServices
                    variant="white"
                    heading="Our Android App Development Services"
                    subheading="Our Android app development services help businesses turn ideas and requirements into reliable mobile products. We build solutions around your business goals and user needs, whether you are launching a new application, improving an existing product, or expanding your digital capabilities."
                    imageSrc={androidServicesImage}
                    imageCaptionTitle={androidServicesImageCaption}
                    services={androidServices}
                />
            </div>

            <HomeIndustryTabs
                heading="Industry-Specific Android App Development Solutions"
                subheading="Different industries have different users, workflows, and technology requirements. We develop Android solutions around these business realities, helping organisations improve customer experiences, streamline operations, and connect <a href='/mobile-application-development-services' target='_blank'>mobile applications</a> with their existing systems."
                data={androidIndustries}
                variant="overlay"
                headerAlign="center"
                wideHeading
            />

            <div className={`top-space bottom-spacing mobile-top-padding mobile-bottom-padding ${sectionBg.lightGradient}`}>
                <IosSuccessStories
                    tone="light"
                    heading="Android App Development Success Stories"
                    stories={successStories}
                    onEnquire={openForm}
                />
            </div>

            <div className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
                <MvpTestimonials heading="Testimonials" testimonials={testimonials} />
            </div>

            {/* The showcase brings its own section padding and white ground; the wrapper swaps that for the gradient. */}
            <div className={`${sectionBg.lightGradient} ${androidStyles.transparentSection}`}>
                <HomeExpertiseShowcase
                    heading="AI Integration for Android Applications"
                    subheading="We <a href='/ai-development-company' target='_blank'>integrate AI</a> capabilities into Android applications to automate repetitive work, personalize user experiences, surface relevant insights, and support faster decisions. Each implementation is aligned with your business workflows, data, and systems while maintaining security, scalability, and production reliability."
                    panels={androidAiCapabilities}
                />
            </div>

            <div className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
                <FeatureIntegrationTabs
                    heading="Android App Development Features & Enterprise Integrations"
                    subheading="Build Android applications with the capabilities required for secure user access, connected workflows, real-time operations, payments, analytics, and enterprise system integration."
                    featuresLabel="Core App Features"
                    features={androidFeatures}
                    chipsTitle="Enterprise Integrations"
                    chips={androidFeatureChips}
                    integrationsLabel="Enterprise Integrations"
                    integrations={androidIntegrations}
                />
            </div>

            <div className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding bg-blue-background">
                <MvpProcess
                    heading="Android App Development Process for Enterprise-Grade Solutions"
                    subheading="We take Android products from initial requirements through development, launch, and ongoing support using a structured delivery process. Each stage keeps priorities clear, improves visibility, reduces delivery risk, and prepares the application for reliable production use."
                    steps={androidProcessSteps}
                />
            </div>

            <div className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
                <HomeImageCTA
                    heading="Need Experienced Android Engineers for Your Next Release?"
                    subheading="Add the technical capacity needed to build, modernize, or scale complex Android applications."
                    buttonText="Hire Android App Developers"
                    imageSrc={androidEngineersCtaImage}
                    imageAlt="Android engineers reviewing an app build beside cloud and code diagrams"
                    openForm="/hire-android-app-developers"
                    meetingLink
                />
            </div>

            <div className={`top-space bottom-spacing mobile-top-padding mobile-bottom-padding ${sectionBg.lightGradient}`}>
                <AiStackTable
                    wideHeading
                    heading="Tech Stack We Use for Android App Development"
                    subheading="We select technologies based on your product requirements, performance, security, integrations, scalability, device capabilities, and long-term maintainability."
                    contentData={androidTechStackRows}
                />
            </div>

            <div className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
                <ComplianceBadgeTabs
                    light
                    grouped
                    heading="Security & Compliance Standards We Address in Android App Development"
                    subheading="Android applications can be designed to align with relevant privacy, security, accessibility, and regulatory requirements across enterprise, <a href='/healthcare-app-development-company' target='_blank'>healthcare</a>, fintech, and consumer use cases."
                    groups={androidComplianceGroups}
                />
            </div>

            <div className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding bg-blue-background">
                <MvpWhyChoose
                    heading="Why Choose Debut Infotech as Your Android App Development Company?"
                    subheading="Choosing an enterprise Android app development partner means evaluating more than coding capability. <a href='/' target='_blank'>Debut Infotech</a> brings multidisciplinary expertise across mobile, backend, cloud, AI, <a href='/software-testing-services' target='_blank'>QA</a>, and <a href='/devops' target='_blank'>DevOps</a> to support Android products from architecture through long-term evolution."
                    cards={androidWhyChooseCards}
                />
            </div>

            <div className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
                <MvpCostTimeline
                    heading="Android App Development Cost & Timeline"
                    subheading={
                        <>
                            Custom Android app development typically starts around <strong>$15,000 for a focused MVP</strong>. Enterprise
                            applications with complex integrations, AI, offline workflows, or regulated data can exceed{' '}
                            <strong>$100,000–$150,000+</strong>.
                        </>
                    }
                    highlight={androidCostHighlight}
                    estimator={androidCostEstimator}
                />
            </div>

            <div className="bottom-spacing mobile-bottom-padding">
                <HomeImageCTA
                    heading="Turn Your Android App Vision Into a Production-Ready Product"
                    subheading="Looking for a company to develop a custom Android application around your workflows, integrations, and growth plans?"
                    buttonText="Schedule Free Consultation"
                    imageSrc={androidFinalCtaImage}
                    imageAlt="Android phone with app screens, an Android robot, and connected service panels"
                    openForm={openForm}
                />
            </div>

            <div className="top-space mobile-top-padding bottom-spacing mobile-bottom-padding">
                <HomeFAQSplit
                    heading="Android App Development Questions"
                    subheading="Answers to common questions from product teams, startups, and enterprise leaders evaluating Android app development services."
                    faqData={androidFaqData}
                    capCardOnTablet
                />
            </div>
            <div className="latest-insights top-space bottom-spacing bg-blue-background mobile-top-padding mobile-bottom-padding">
                <LatestInsights id="18" />
            </div>

            {/* Generated from the same array the section renders, so the schema always
                matches the visible answers. */}
            <script
                type="application/ld+json"
                id="android-app-development-faq"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'FAQPage',
                        mainEntity: androidFaqData
                            .flatMap((category: {questions: {q: string; a: string}[]}) => category.questions)
                            .map((item: {q: string; a: string}) => ({
                                '@type': 'Question',
                                name: item.q,
                                acceptedAnswer: {'@type': 'Answer', text: stripHtmlForJsonLd(item.a)}
                            }))
                    })
                }}
            />
        </div>
    );
};

// Every data list is read at build time, so none of the data modules ship in this page's client bundle.
export async function getStaticProps() {
    const [{clients}, {mvpTestimonials}, {getAndroidSuccessStories}, androidData] = await Promise.all([
        import('@/util/contents/home-data'),
        import('@/util/contents/mvp-development-data'),
        import('@/util/contents/android-success-stories'),
        import('@/util/contents/android-app-development')
    ]);
    return {
        props: {
            clients,
            testimonials: mvpTestimonials,
            androidHeroImage: androidData.androidHeroImage,
            androidHeroAspectRatio: androidData.ANDROID_HERO_ASPECT_RATIO,
            androidTrustStats: androidData.androidTrustStats,
            androidServicesImage: androidData.androidServicesImage,
            androidServicesImageCaption: androidData.androidServicesImageCaption,
            androidServices: androidData.androidServices,
            androidIndustries: androidData.androidIndustries,
            successStories: getAndroidSuccessStories(),
            androidAiCapabilities: androidData.androidAiCapabilities,
            androidFeatures: androidData.androidFeatures,
            androidFeatureChips: androidData.androidFeatureChips,
            androidIntegrations: androidData.androidIntegrations,
            androidProcessSteps: androidData.androidProcessSteps,
            androidEngineersCtaImage: androidData.androidEngineersCtaImage,
            androidTechStackRows: androidData.androidTechStackRows,
            androidComplianceGroups: androidData.androidComplianceGroups,
            androidWhyChooseCards: androidData.androidWhyChooseCards,
            androidCostHighlight: androidData.androidCostHighlight,
            androidCostEstimator: androidData.androidCostEstimator,
            androidFinalCtaImage: androidData.androidFinalCtaImage,
            androidFaqData: androidData.androidFaqData
        }
    };
}

export default AndroidAppDevelopment;
AndroidAppDevelopment.Layout = 'Main';
